/**
 * @file index.tsx
 *
 * @fileoverview Displays a dismissible alert on the screen.
 */
import {
  Content,
  Overlay,
  Portal,
  Root,
  Title,
} from "@radix-ui/react-alert-dialog";
import { ReactNode, useRef } from "react";
import {
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../theme/spacing";
import Button from "../01-forms/01-button";
import FlexItems from "../06-layout/03-flex";
import FlexItem from "../06-layout/03-flex/02-flex-item";
import Spacing from "../06-layout/07-spacing";
import styles from "./Alert.module.scss";

type BaseAlertProps = SpaceProps;

export interface AlertProps extends BaseAlertProps {
  /**
   * Contents to render inside the modal.
   */
  children: ReactNode;
  /**
   * Content for the display of confirm.
   */
  confirmText: ReactNode;
  /**
   * Content for the display of cancel.
   */
  cancelText: ReactNode;
  /**
   * Content for the display of cancel.
   */
  alertLabel: ReactNode;
  /**
   * If the modal is currently visible.
   */
  isShown: boolean;
  /**
   * Callback for when closing or cancelling.
   */
  onClose: () => void;
  /**
   * Callback for when confirming.
   */
  onConfirm: () => void;
}

export default function Alert({
  children,
  isShown,
  onClose,
  onConfirm,
  confirmText,
  cancelText,
  alertLabel,
  ...props
}: AlertProps) {
  // we'll pass this ref to both AlertDialog and our button
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <Root open={isShown}>
      <Portal>
        <Overlay className={styles.alertOveraly} />
        <Content
          className={`
          ${styles.alertContent}
          ${generateMarginClasses(props, styles)}
          ${generatePaddingClasses(props, styles)}
          `.trim()}
        >
          <Title>
            <div className={styles.alertLabel}>{alertLabel}</div>
          </Title>
          <Spacing size="medium" />
          <div>{children}</div>
          <Spacing size="medium" />
          <FlexItems
            flexDirection={["column", "row"]}
            alignItems="center"
            flexWrap="wrap"
            justifyContent="center"
            gap={[4, 6]}
          >
            <FlexItem>
              <Button
                buttonStyle="primary"
                raised={true}
                onClick={onConfirm}
                data-testid="alert-confirm-button"
              >
                {confirmText}
              </Button>
            </FlexItem>
            <FlexItem>
              <Button
                buttonStyle="danger"
                raised={false}
                ref={cancelRef}
                onClick={onClose}
                data-testid="alert-cancel-button"
              >
                {cancelText}
              </Button>
            </FlexItem>
          </FlexItems>
        </Content>
      </Portal>
    </Root>
  );
}
export { Alert };
